import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FaClock, FaDumbbell, FaArrowLeft, FaPlay, FaRedo, FaInfoCircle, FaListOl, FaStopwatch, FaYoutube, FaTimes, FaCopy, FaCalendarPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { getActiveWorkoutDraft, getStoredWorkoutUserId } from '../../utils/activeWorkoutDrafts';
import { cloneWorkout, deleteUserWorkout } from '../../services/fitnessService';
import { cloneCoachWorkout } from '../../services/coachFitnessService';
import ScheduleModal from './ScheduleModal';
import ConfirmModal from '../ConfirmModal';
import toast from 'react-hot-toast';
import SEO from '../SEO';
import { absoluteUrl, buildBreadcrumbs } from '../../utils/seoConfig';
import { trackWorkoutStarted } from '../../utils/analytics';
import './WorkoutPage.css';

const draftHasMeaningfulProgress = (draft) => {
  if (!draft) return false;
  if ((draft.progress ?? 0) > 0 || (draft.completedExercises ?? 0) > 0) return true;
  const logs = draft.exerciseLogs || {};
  return Object.values(logs).some(
    (log) =>
      log?.skipped ||
      log?.completed ||
      log?.sets?.some((s) => s?.completed || s?.weight != null || (s?.reps != null && s.reps !== ''))
  );
};

const WorkoutPage = () => {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('scheduleId');
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cascadeExercises, setCascadeExercises] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  useEffect(() => {
    fetchWorkout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId]);

  const fetchWorkout = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/workouts/${workoutId}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (!response.ok) throw new Error('Failed to fetch workout');
      const data = await response.json();
      setWorkout(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins} min`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getExerciseMetric = (exercise) => {
    if (exercise.duration) {
      return { type: 'time', value: formatDuration(exercise.duration) };
    }
    if (exercise.reps) {
      return { type: 'reps', value: `${exercise.reps} reps` };
    }
    const details = exercise.exerciseDetails;
    if (details?.defaultDuration) {
      return { type: 'time', value: formatDuration(details.defaultDuration) };
    }
    if (details?.defaultReps) {
      return { type: 'reps', value: `${details.defaultReps} reps` };
    }
    return { type: 'unknown', value: '—' };
  };

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isCoach = user?.isAffiliate && user?.fitnessEnabled;
  const canEditOwnedWorkout = Boolean(
    user && !isCoach && workout?.userId && workout.userId === user.sub && workout._id
  );
  const canDeleteWorkout = Boolean(
    user &&
      workout?._id &&
      (user.isAdmin || (!isCoach && workout.userId && workout.userId === user.sub))
  );

  const resumableDraft = useMemo(() => {
    if (!workout) return null;
    const uid = getStoredWorkoutUserId();
    const byParam = getActiveWorkoutDraft({ userId: uid, workoutId, scheduleId });
    const slug = workout.workoutId;
    const bySlug =
      slug && slug !== workoutId ? getActiveWorkoutDraft({ userId: uid, workoutId: slug, scheduleId }) : null;
    const d = byParam || bySlug;
    return draftHasMeaningfulProgress(d) ? d : null;
  }, [workout, workoutId, scheduleId]);

  const handleDeleteWorkout = async () => {
    if (!workout?._id) return;
    setDeleteLoading(true);
    try {
      const data = await deleteUserWorkout(workout._id, { cascadeExercises });
      const n = data.cascade?.exercisesDeleted ?? 0;
      toast.success(
        n > 0
          ? `Workout deleted. Also removed ${n} exercise${n !== 1 ? 's' : ''} you were allowed to delete.`
          : 'Workout deleted'
      );
      setDeleteModalOpen(false);
      setCascadeExercises(false);
      navigate('/fitness');
    } catch (err) {
      toast.error(err.message || 'Failed to delete workout');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClone = async () => {
    try {
      if (isCoach) {
        const cloned = await cloneCoachWorkout(workoutId);
        toast.success('Workout cloned to your coach library!');
        navigate(`/coach/workout/${cloned._id}/edit`);
      } else {
        const cloned = await cloneWorkout(workoutId);
        toast.success('Workout cloned! Redirecting to editor...');
        navigate(`/fitness/workout/${cloned._id}/edit`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to clone workout');
    }
  };

  const handleStartWorkout = () => {
    const routeId = resumableDraft?.workoutId || workout?.workoutId || workoutId;
    const qs = scheduleId ? `?scheduleId=${encodeURIComponent(scheduleId)}` : '';
    trackWorkoutStarted(routeId, workout?.programId);
    navigate(`/fitness/active/${routeId}${qs}`);
  };

  const handleExerciseClick = (exerciseId) => {
    navigate(`/fitness/exercise/${exerciseId}`);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1` : null;
  };

  const handleVideoClick = (e, exercise) => {
    e.stopPropagation();
    const details = exercise.exerciseDetails;
    const embedUrl = getYouTubeEmbedUrl(details?.video);
    if (embedUrl) {
      setVideoModal({ url: embedUrl, name: details?.name || 'Exercise' });
    }
  };

  const canEditExerciseDetails = (details) =>
    Boolean(user?.sub && details?._id && details?.userId && details.userId === user.sub);

  const handleExerciseEditClick = (e, details) => {
    e.stopPropagation();
    if (!details?._id) return;
    const path = isCoach ? `/coach/exercise/${details._id}/edit` : `/fitness/exercise/${details._id}/edit`;
    navigate(path);
  };

  const renderExerciseCard = (exercise, blockName, roundInfo = null) => {
    const details = exercise.exerciseDetails;
    const metric = getExerciseMetric(exercise);
    const sets = exercise.sets || 1;

    return (
      <div 
        key={`${blockName}-${exercise.exerciseId}-${exercise.order}`}
        className="exercise-card"
        onClick={() => handleExerciseClick(exercise.exerciseId)}
      >
        <div className="exercise-left">
          <div className="exercise-image-placeholder">
            {details?.image ? (
              <img src={details.image} alt={details?.name} />
            ) : (
              <FaDumbbell />
            )}
          </div>
        </div>

        <div className="exercise-middle">
          <h4>{details?.name || 'Exercise'}</h4>
          <div className="exercise-params">
            {sets > 1 && (
              <span className="param">
                <FaListOl />
                {sets} sets
              </span>
            )}
            <span className="param">
              {metric.type === 'time' ? <FaStopwatch /> : <FaDumbbell />}
              {metric.value}
            </span>
            {exercise.restAfter > 0 && (
              <span className="param rest">
                {formatDuration(exercise.restAfter)} rest
              </span>
            )}
          </div>
          {exercise.notes && (
            <p className="exercise-notes">
              <FaInfoCircle />
              {exercise.notes}
            </p>
          )}
        </div>

        <div className="exercise-right">
          <div className="exercise-card-actions">
            {canEditExerciseDetails(details) && (
              <button
                type="button"
                className="exercise-edit-btn"
                onClick={(e) => handleExerciseEditClick(e, details)}
                title="Edit exercise"
              >
                <FaEdit />
              </button>
            )}
            {details?.video && (
              <button
                type="button"
                className="exercise-video-btn"
                onClick={(e) => handleVideoClick(e, exercise)}
                title="Watch demonstration"
              >
                <FaYoutube />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBlock = (block, blockIndex, isWarmup = false, isCooldown = false) => {
    if (!block || !block.exercises || block.exercises.length === 0) return null;

    const blockClass = isWarmup ? 'warmup' : isCooldown ? 'cooldown' : '';

    return (
      <div key={`block-${blockIndex}`} className={`workout-block ${blockClass}`}>
        <div className="block-header">
          <h3>{block.name}</h3>
          {block.rounds > 1 && (
            <span className="rounds-badge">{block.rounds} rounds</span>
          )}
        </div>
        {block.description && (
          <p className="block-description">{block.description}</p>
        )}
        {block.restBetweenRounds > 0 && block.rounds > 1 && (
          <p className="rest-between">
            Rest {formatDuration(block.restBetweenRounds)} between rounds
          </p>
        )}
        <div className="exercises-list">
          {block.exercises.map((exercise) => renderExerciseCard(exercise, block.name))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="workout-page">
        <div className="workout-loading">
          <div className="loading-spinner" />
          <p>Loading workout...</p>
        </div>
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="workout-page">
        <div className="workout-error">
          <p>Error loading workout: {error || 'Workout not found'}</p>
          <button onClick={() => navigate('/fitness')}>Back to Programs</button>
        </div>
      </div>
    );
  }

  const workoutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ExercisePlan',
    name: workout.name,
    description: workout.goal || workout.description || `${workout.name} workout`,
    url: absoluteUrl(`/fitness/workout/${workout._id}`),
    exerciseType: workout.category,
    activityDuration: workout.duration ? `PT${workout.duration}M` : undefined,
    additionalProperty: [
      ...(workout.difficulty ? [{ '@type': 'PropertyValue', name: 'Difficulty', value: workout.difficulty }] : []),
      ...(workout.equipment || []).map((e) => ({ '@type': 'PropertyValue', name: 'Equipment', value: e })),
    ],
  };

  return (
    <div className="workout-page">
      <SEO
        title={`${workout.name} — Workout with Exercises, Sets & Reps`}
        description={
          (workout.goal || workout.description || '').slice(0, 200) ||
          `${workout.name} workout — exercises, sets, reps, and rest periods on FreshFitLab.`
        }
        canonical={`/fitness/workout/${workout._id}`}
        type="article"
        jsonLd={[
          workoutJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Workouts', url: '/fitness/workouts' },
            { name: workout.name, url: `/fitness/workout/${workout._id}` },
          ]),
        ]}
      />
      <div className="workout-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
          <span>Back</span>
        </button>

        <div className="workout-header-content">
          <div className="workout-category-badge">{workout.category}</div>
          <h1>{workout.name}</h1>
          <p className="workout-goal">{workout.goal}</p>

          <div className="workout-stats">
            <div className="stat">
              <FaClock />
              <span>{workout.estimatedDuration} min</span>
            </div>
            <div className="stat">
              <FaDumbbell />
              <span>{workout.totalExercises} exercises</span>
            </div>
            <div className="stat difficulty">
              <span>{workout.difficulty}</span>
            </div>
          </div>

          <div className="workout-action-buttons">
            <button type="button" className="start-workout-btn" onClick={handleStartWorkout}>
              {resumableDraft ? <FaRedo /> : <FaPlay />}
              <span>{resumableDraft ? 'Continue workout' : 'Start workout'}</span>
            </button>
            {user && (
              <>
                {canEditOwnedWorkout && (
                  <button
                    type="button"
                    className="clone-workout-btn"
                    onClick={() => navigate(`/fitness/workout/${workout._id}/edit`)}
                  >
                    <FaEdit />
                    <span>Edit workout</span>
                  </button>
                )}
                <button className="clone-workout-btn" onClick={handleClone}>
                  <FaCopy />
                  <span>Clone & Customize</span>
                </button>
                <button className="clone-workout-btn" onClick={() => setShowSchedule(true)}>
                  <FaCalendarPlus />
                  <span>Schedule</span>
                </button>
                {canDeleteWorkout && (
                  <button
                    type="button"
                    className="workout-delete-btn"
                    onClick={() => {
                      setCascadeExercises(false);
                      setDeleteModalOpen(true);
                    }}
                  >
                    <FaTrash />
                    <span>Delete</span>
                  </button>
                )}
              </>
            )}
          </div>

          {showSchedule && workout && (
            <ScheduleModal
              type="workout"
              itemId={workout.workoutId || workoutId}
              itemName={workout.name}
              onClose={() => setShowSchedule(false)}
            />
          )}
        </div>
      </div>

      <div className="workout-container">
        {/* Equipment Needed */}
        {workout.equipmentNeeded && workout.equipmentNeeded.length > 0 && (
          <section className="equipment-needed">
            <h3>Equipment Needed</h3>
            <div className="equipment-tags">
              {workout.equipmentNeeded.map((item, idx) => (
                <span key={idx} className="equipment-tag">{item}</span>
              ))}
            </div>
          </section>
        )}

        {/* Workout Structure */}
        <div className="workout-structure">
          {/* Warmup */}
          {renderBlock(workout.warmup, 'warmup', true, false)}

          {/* Main Blocks */}
          {workout.blocks?.map((block, idx) => renderBlock(block, idx))}

          {/* Cooldown */}
          {renderBlock(workout.cooldown, 'cooldown', false, true)}
        </div>

        {/* Target Areas */}
        {workout.targetAreas && workout.targetAreas.length > 0 && (
          <section className="target-areas">
            <h3>Target Areas</h3>
            <div className="target-tags">
              {workout.targetAreas.map((area, idx) => (
                <span key={idx} className="target-tag">{area}</span>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Floating Start Button */}
      <div className="floating-start">
        <button type="button" className="floating-start-btn" onClick={handleStartWorkout}>
          {resumableDraft ? <FaRedo /> : <FaPlay />}
          <span>{resumableDraft ? 'Continue workout' : 'Start workout'}</span>
        </button>
      </div>

      {/* Video Modal */}
      {videoModal && (
        <div className="video-modal-overlay" onClick={() => setVideoModal(null)}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>{videoModal.name}</h3>
              <button className="video-modal-close" onClick={() => setVideoModal(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="video-modal-body">
              <iframe
                src={videoModal.url}
                title={videoModal.name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModalOpen}
        variant="dark"
        title="Delete this workout?"
        message="This permanently removes the workout. Programs that reference it may show a gap until you edit them. Cascading exercises only removes ones you are allowed to delete."
        confirmLabel="Delete workout"
        cancelLabel="Cancel"
        danger
        loading={deleteLoading}
        loadingLabel="Deleting…"
        onConfirm={handleDeleteWorkout}
        onCancel={() => !deleteLoading && setDeleteModalOpen(false)}
      >
        <label>
          <input
            type="checkbox"
            checked={cascadeExercises}
            disabled={deleteLoading}
            onChange={(e) => setCascadeExercises(e.target.checked)}
          />
          <span>Also delete exercises I created that appear in this workout</span>
        </label>
      </ConfirmModal>
    </div>
  );
};

export default WorkoutPage;

